import { supabase, invokeFunction } from '@/lib/supabase';
import type { TablesUpdate } from '@repo/supabase-client';
import { addBreadcrumb } from '@/lib/sentry';
import { parseInvokeErrorBody } from '@/lib/invoke-error';
import { scalarFromLinks, type LinkEntry } from '@/lib/links';
import { resolveActiveMembership } from '@/services/household.service';
import {
  isMissingFunctionError,
  isSchemaNotReadyError,
} from '@/lib/deployWindow';
import { quotaExceededMessage } from '@/constants/billing';
import type {
  Email,
  EmailFilters,
  EmailExtraction,
  EmailApproveInput,
  ReviewItem,
  TaskStatus,
} from '@/types';
import { recurrenceToJson, type Recurrence } from '@/lib/recurrence';

export type { ReviewItem };

type ReviewItemPatch = TablesUpdate<'review_items'>;

/**
 * What a row reads as before the reading-state migration has landed (deploy
 * window only): no sender name, no recorded failures, no claim stamp.
 */
const READING_STATE_DEFAULTS = {
  sender_label: null,
  failure_count: 0,
  processing_started_at: null,
} as const;

export async function getEmails(
  householdId: string,
  filters?: EmailFilters,
): Promise<Email[]> {
  const { data: inboxes, error: inboxError } = await supabase
    .from('inbox_addresses')
    .select('id')
    .eq('household_id', householdId);

  if (inboxError) throw inboxError;
  const inboxIds = (inboxes ?? []).map((i) => i.id);
  if (!inboxIds.length) return [];

  // Body columns (raw_content, processed_content) stay off the list wire —
  // the user hasn't opened the email yet. preprocessing_metadata is kept for
  // list-render metadata (attachment filenames, sanitization flags, timing),
  // but its `images` entry is stripped below: that array holds every
  // `<img src>` and alt text taken verbatim from the body, which is body
  // content by any reading, and on a flyer-heavy inbox those are per-recipient
  // tracking URLs. No list surface renders them.
  // `sender_label`, `failure_count` and `processing_started_at` are the
  // reading-state columns (YAR-477): the sender name the UI may show (never
  // an address), how many times the email has failed, and when the current
  // read started. All three are granted to `authenticated`.
  const SAFE_COLUMNS =
    'id, inbox_address_id, from_email, subject, status, created_at, updated_at, provider_message_id, message_id, household_id, rejection_reason, preprocessing_metadata, sender_label, failure_count, processing_started_at';
  // Deploy-window guard (see lib/deployWindow.ts): the list before the
  // reading-state columns existed. Delete once 20260927190651 is everywhere.
  const LEGACY_SAFE_COLUMNS =
    'id, inbox_address_id, from_email, subject, status, created_at, updated_at, provider_message_id, message_id, household_id, rejection_reason, preprocessing_metadata';

  let query = supabase
    .from('emails')
    .select(SAFE_COLUMNS)
    .in('inbox_address_id', inboxIds)
    .not('status', 'in', '("SYSTEM","REJECTED","DUPLICATE")')
    .order('created_at', { ascending: false });

  if (filters?.status) {
    query = query.eq('status', filters.status);
  }

  let { data, error } = await query;

  if (error && isSchemaNotReadyError(error)) {
    let legacy = supabase
      .from('emails')
      .select(LEGACY_SAFE_COLUMNS)
      .in('inbox_address_id', inboxIds)
      .not('status', 'in', '("SYSTEM","REJECTED","DUPLICATE")')
      .order('created_at', { ascending: false });
    if (filters?.status) legacy = legacy.eq('status', filters.status);
    const fallback = await legacy;
    data = (fallback.data ?? []).map((row) => ({
      ...row,
      ...READING_STATE_DEFAULTS,
    }));
    error = fallback.error;
  }

  if (error) throw error;
  // Spread missing columns as null so the return type satisfies Email (= Tables<'emails'>).
  // processed_disposition isn't needed by list-render call sites, so we
  // omit it from SAFE_COLUMNS above and stub it null here to satisfy
  // the row shape contract.
  // sender_match is ingestion provenance (which header authorised the
  // message, plus Gmail's SPF/DKIM/DMARC verdicts). It stays OFF the wire and
  // is stubbed here.
  //
  // It was briefly selected, to read `sender_org` off it for the Review card
  // (YAR-427), and that was wrong twice over. `authenticated` holds
  // column-level SELECT on `emails` that does not include this column, so
  // every list query died with `42501 permission denied for table emails` —
  // the whole Review tab, for every signed-in user. And the narrowing that
  // was supposed to make it safe ran in the BROWSER: selecting the column
  // ships the auth verdicts to the client and deletes them after they arrive,
  // which is not a control at all.
  //
  // `sender_org` belongs somewhere `authenticated` may already read. See
  // YAR-427 for the follow-up.
  // held_* / hold_count are pipeline bookkeeping for emails waiting out a
  // transient LLM failure. A HELD email renders as Reading (YAR-477) — the
  // hold itself is invisible by design, see failure-policy.ts — so no client
  // reads these; they stay off the wire and are stubbed here.
  // was_truncated / has_images are ingestion instrumentation: they exist to
  // slice extraction misses ("was the body cut", "was the content an image")
  // when reading eval results server-side. Like sender_match above, no client
  // surface renders them, so they stay off the wire and are stubbed here.
  return (data ?? []).map((row) => ({
    ...row,
    preprocessing_metadata: stripBodyDerived(row.preprocessing_metadata),
    raw_content: null,
    processed_content: null,
    content_hash: null,
    processed_disposition: null,
    sender_match: null,
    held_reason: null,
    held_at: null,
    held_until: null,
    hold_count: null,
    was_truncated: null,
    has_images: null,
  }));
}

/** One email as the status toast sees it (lib/emailStatusRun.ts `RunEmail`). */
export interface EmailStatusFeedRow {
  id: string;
  status: string;
  failureCount: number;
  senderLabel: string | null;
  /** `emails.subject` — the Review emails card's row title (YAR-553). */
  subject: string | null;
  createdAt: string;
  readingStartedAt: string | null;
  itemCount: number;
}

/** A finished result stays eligible for the toast this long. */
export const STATUS_FEED_RESULT_WINDOW_MS = 10 * 60_000;
/** An unacknowledged failure stays eligible this long. */
export const STATUS_FEED_FAILURE_WINDOW_MS = 24 * 60 * 60_000;

/**
 * The emails the email status toast narrates (YAR-477): everything still
 * being read, failures from the last day, and anything else that changed in
 * the last few minutes — scoped to the household's inboxes like the Review
 * feed. Emails the PARENT finished (approved / dismissed) are not part of a
 * reading run and are dropped; a PROCESSED email that found nothing
 * (NO_ITEMS) stays, as "Nothing found".
 */
export async function getEmailStatusFeed(
  householdId: string,
  now: number = Date.now(),
): Promise<EmailStatusFeedRow[]> {
  const { data: inboxes, error: inboxError } = await supabase
    .from('inbox_addresses')
    .select('id')
    .eq('household_id', householdId);
  if (inboxError) throw inboxError;
  const inboxIds = (inboxes ?? []).map((i) => i.id);
  if (!inboxIds.length) return [];

  const resultsSince = new Date(
    now - STATUS_FEED_RESULT_WINDOW_MS,
  ).toISOString();
  const failuresSince = new Date(
    now - STATUS_FEED_FAILURE_WINDOW_MS,
  ).toISOString();
  const window = `updated_at.gte.${resultsSince},status.in.(RECEIVED,PROCESSING,HELD),and(status.eq.ERROR,updated_at.gte.${failuresSince})`;

  const STATUS_FEED_COLUMNS =
    'id, status, subject, created_at, updated_at, processed_disposition, sender_label, failure_count, processing_started_at';
  // Deploy-window guard (lib/deployWindow.ts).
  const LEGACY_STATUS_FEED_COLUMNS =
    'id, status, subject, created_at, updated_at, processed_disposition';

  type FeedRow = {
    id: string;
    status: string;
    subject: string | null;
    created_at: string;
    updated_at: string;
    processed_disposition: string | null;
    sender_label?: string | null;
    failure_count?: number;
    processing_started_at?: string | null;
  };

  let rows: FeedRow[];
  const primary = await supabase
    .from('emails')
    .select(STATUS_FEED_COLUMNS)
    .in('inbox_address_id', inboxIds)
    .not('status', 'in', '("SYSTEM","REJECTED","DUPLICATE")')
    .or(window)
    .order('created_at', { ascending: true })
    .limit(100);
  if (primary.error && isSchemaNotReadyError(primary.error)) {
    const legacy = await supabase
      .from('emails')
      .select(LEGACY_STATUS_FEED_COLUMNS)
      .in('inbox_address_id', inboxIds)
      .not('status', 'in', '("SYSTEM","REJECTED","DUPLICATE")')
      .or(window)
      .order('created_at', { ascending: true })
      .limit(100);
    if (legacy.error) throw legacy.error;
    rows = legacy.data ?? [];
  } else {
    if (primary.error) throw primary.error;
    rows = primary.data ?? [];
  }

  const relevant = rows.filter(
    (r) => r.status !== 'PROCESSED' || r.processed_disposition === 'NO_ITEMS',
  );
  const counts = await getReviewItemCounts(relevant.map((r) => r.id));
  return relevant.map((r) => ({
    id: r.id,
    status: r.status,
    failureCount: r.failure_count ?? 0,
    senderLabel: r.sender_label ?? null,
    subject: r.subject ?? null,
    createdAt: r.created_at,
    readingStartedAt: r.processing_started_at ?? r.updated_at,
    itemCount: counts.get(r.id) ?? 0,
  }));
}

export async function getEmail(emailId: string): Promise<Email | null> {
  // Detail fetch — exclude only raw_content (the RFC822 source, blocked at
  // the DB layer by migration 20260414000001). Body fields are required for
  // the Source Reference section in the review modal, where the user is
  // actively reviewing this specific email and is already authorized for it
  // via household RLS. List-level fetches (see listEmails above) still use
  // a narrower allowlist since list views never render the body.
  const DETAIL_COLUMNS =
    'id, inbox_address_id, from_email, subject, status, created_at, updated_at, provider_message_id, message_id, household_id, rejection_reason, processed_content, preprocessing_metadata, content_hash, sender_label, failure_count, processing_started_at';
  // Deploy-window guard — see getEmails.
  const LEGACY_DETAIL_COLUMNS =
    'id, inbox_address_id, from_email, subject, status, created_at, updated_at, provider_message_id, message_id, household_id, rejection_reason, processed_content, preprocessing_metadata, content_hash';

  let { data, error } = await supabase
    .from('emails')
    .select(DETAIL_COLUMNS)
    .eq('id', emailId)
    .maybeSingle();

  if (error && isSchemaNotReadyError(error)) {
    const fallback = await supabase
      .from('emails')
      .select(LEGACY_DETAIL_COLUMNS)
      .eq('id', emailId)
      .maybeSingle();
    data = fallback.data
      ? { ...fallback.data, ...READING_STATE_DEFAULTS }
      : null;
    error = fallback.error;
  }

  if (error) throw error;
  // processed_disposition is column-scoped: the review modal doesn't
  // surface dismiss-vs-approve in the email card so it stays out of
  // DETAIL_COLUMNS; stub null to satisfy the row shape.
  return data
    ? {
        ...data,
        raw_content: null,
        processed_disposition: null,
        // See getEmails: narrowed to the organisation, auth verdicts dropped.
        sender_match: null,
        // See listEmails: ingestion instrumentation, server-side slicing only.
        was_truncated: null,
        has_images: null,
        // See listEmails: hold bookkeeping, no client surface reads it.
        held_reason: null,
        held_at: null,
        held_until: null,
        hold_count: null,
      }
    : null;
}

export async function getExtraction(
  emailId: string,
): Promise<EmailExtraction | null> {
  const { data, error } = await supabase
    .from('email_extractions')
    .select()
    .eq('email_id', emailId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Thrown when `emails-approve` rejects with a 403 QUOTA_EXCEEDED. The
 * UI uses the typed fields (tier / used / quota) to render a
 * limit-reached banner with an Upgrade CTA, rather than the generic
 * error message.
 */
export class QuotaExceededError extends Error {
  readonly code = 'QUOTA_EXCEEDED' as const;
  readonly tier: 'FREE' | 'CORE' | 'FAMILY';
  readonly used: number;
  readonly quota: number;
  constructor(tier: 'FREE' | 'CORE' | 'FAMILY', used: number, quota: number) {
    super(quotaExceededMessage(tier, used, quota));
    this.name = 'QuotaExceededError';
    this.tier = tier;
    this.used = used;
    this.quota = quota;
  }
}

export async function approveEmail(
  emailId: string,
  input?: EmailApproveInput,
): Promise<void> {
  addBreadcrumb('email.approve_started', {
    email_id: emailId,
    has_child_id: !!input?.child_id,
    has_item_member_ids: !!input?.item_member_ids,
  });
  const { data, error } = await invokeFunction('emails-approve', {
    body: {
      id: emailId,
      child_id: input?.child_id,
      // Per-item household-member assignments picked in the review
      // modal. Map<review_item_id, user_id[]>. Omitting it (or passing
      // an empty object) preserves the pre-member-assignment behavior.
      item_member_ids: input?.item_member_ids,
    },
  });

  if (error) {
    // FunctionsHttpError doesn't expose the 4xx body directly — we
    // have to parse Response.json() ourselves. Promote the 403
    // QUOTA_EXCEEDED into a typed error so the modal can render the
    // limit-reached state without string-sniffing.
    const body = await parseInvokeErrorBody(error);
    if (body && body.code === 'QUOTA_EXCEEDED') {
      const tier = body.tier;
      const used = body.used;
      const quota = body.quota;
      if (
        (tier === 'FREE' || tier === 'CORE' || tier === 'FAMILY') &&
        typeof used === 'number' &&
        typeof quota === 'number'
      ) {
        addBreadcrumb('email.approve_quota_exceeded', { tier, used, quota });
        throw new QuotaExceededError(tier, used, quota);
      }
    }
    addBreadcrumb('email.approve_failed', { email_id: emailId });
    throw error;
  }

  // The edge function returns { success, committed, failed } with HTTP 200.
  // Check the body for business-logic failures.
  if (data && typeof data === 'object' && data.success === false) {
    addBreadcrumb('email.approve_items_failed', { email_id: emailId });
    const failedItems = data.failed ?? [];
    const reasons = failedItems
      .map((f: { reason: string }) => f.reason)
      .join(', ');
    throw new Error(`Failed to commit items: ${reasons || 'unknown error'}`);
  }

  addBreadcrumb('email.approve_succeeded', { email_id: emailId });
}

export async function dismissEmail(emailId: string): Promise<void> {
  addBreadcrumb('email.dismiss_started', { email_id: emailId });
  const { error } = await invokeFunction('emails-dismiss', {
    body: { id: emailId },
  });

  if (error) {
    addBreadcrumb('email.dismiss_failed', { email_id: emailId });
    throw error;
  }
  addBreadcrumb('email.dismiss_completed', { email_id: emailId });
}

export interface RetryEmailResult {
  /**
   * False when nothing was re-read: the email was already being read (a
   * double tap is a no-op), or it cannot be read right now (`reason`).
   */
  retried: boolean;
  status: string;
  /**
   * Why a retry did not happen, when the server says. Today only
   * `subscription_paused`: the household's Gmail connection is paused, so
   * the email stays ERROR ("Couldn't be read") rather than going back to
   * RECEIVED, where nothing would ever read it.
   */
  reason?: string;
}

/**
 * Try again on an email Yaruta couldn't read (YAR-477, YAR-171 AC5).
 *
 * One call to the `retry_email` RPC, which does the whole reset atomically on
 * the server: deletes the previous attempt's extraction and review items,
 * puts the email back to RECEIVED with the hold ladder, rejection reason and
 * claim stamp cleared, and enqueues it. It authorizes the caller itself
 * (PRIMARY or CO_ADMIN of the email's household).
 *
 * This used to be three writes from the browser. The last one, the status
 * UPDATE, is owner-only under RLS, so for a Co-admin it matched no rows and
 * returned no error: their Retry deleted the artifacts and left the email in
 * ERROR, looking like a button that did nothing.
 *
 * `.rpc()` resolves with `{ data: null, error }` on a database error rather
 * than throwing, so the error is inspected explicitly.
 */
export async function retryEmail(
  emailId: string,
  householdId: string,
): Promise<RetryEmailResult> {
  addBreadcrumb('email.retry_started', { email_id: emailId });
  const { data, error } = await supabase.rpc('retry_email', {
    p_email_id: emailId,
  });
  if (error && isMissingFunctionError(error)) {
    // Deploy-window guard (lib/deployWindow.ts): the RPC is not on this
    // backend yet, so retry the way the previous release did.
    await legacyRetryEmail(emailId, householdId);
    return { retried: true, status: 'RECEIVED' };
  }
  if (error) {
    addBreadcrumb('email.retry_failed', { email_id: emailId });
    throw error;
  }
  const result =
    data && typeof data === 'object' && !Array.isArray(data) ? data : {};
  return {
    retried: result.retried === true,
    status: typeof result.status === 'string' ? result.status : 'RECEIVED',
    ...(typeof result.reason === 'string' ? { reason: result.reason } : {}),
  };
}

/**
 * The pre-RPC retry, verbatim from the previous release — ONLY for the
 * deploy window before `retry_email` exists. Owner-only under RLS (a
 * Co-admin's status write silently matches no rows), which is the bug the
 * RPC fixes. Delete once 20260927190653 is everywhere.
 */
async function legacyRetryEmail(
  emailId: string,
  householdId: string,
): Promise<void> {
  const { error: extErr } = await supabase
    .from('email_extractions')
    .delete()
    .eq('email_id', emailId);
  if (extErr) throw extErr;

  const { error: riErr } = await supabase
    .from('review_items')
    .delete()
    .eq('email_id', emailId)
    .eq('household_id', householdId);
  if (riErr) throw riErr;

  const { error } = await supabase
    .from('emails')
    .update({
      status: 'RECEIVED',
      rejection_reason: null,
      held_reason: null,
      held_at: null,
      held_until: null,
      hold_count: 0,
    })
    .eq('id', emailId);
  if (error) throw error;
}

/** The two facts an Activity failure row needs before it acts. */
export interface EmailRetryState {
  status: Email['status'];
  failureCount: number;
}

/**
 * Read an email's live status and failure count (YAR-171 AC5).
 *
 * The Activity row's copy is frozen at the moment the notification was
 * written; the email may have been retried, re-read or failed again since.
 * Returns null when the email is gone or not visible to this member.
 */
export async function getEmailRetryState(
  emailId: string,
  householdId: string,
): Promise<EmailRetryState | null> {
  const { data, error } = await supabase
    .from('emails')
    .select('status, failure_count')
    .eq('id', emailId)
    .eq('household_id', householdId)
    .maybeSingle();
  if (error && isSchemaNotReadyError(error)) {
    // Deploy-window guard: no failure_count yet, so every failure reads as
    // the first (retry in place — the previous release's only behaviour).
    const fallback = await supabase
      .from('emails')
      .select('status')
      .eq('id', emailId)
      .eq('household_id', householdId)
      .maybeSingle();
    if (fallback.error) throw fallback.error;
    return fallback.data
      ? { status: fallback.data.status, failureCount: 0 }
      : null;
  }
  if (error) throw error;
  if (!data) return null;
  return { status: data.status, failureCount: data.failure_count ?? 0 };
}

/**
 * What tapping an Activity "couldn't be read" row did (YAR-171 AC5):
 * - `retried`: first failure — re-read in place, stay on Activity;
 * - `reading`: already being read again (a retry from elsewhere) — stay;
 * - `open-review`: a second failure, an email that has moved on, or one we
 *   cannot read — open Review, where the card carries Discard and Try again.
 */
export type FailedEmailTapOutcome = 'retried' | 'reading' | 'open-review';

/** A second failure (or more) sends the parent to Review instead. */
export const FAILURES_BEFORE_REVIEW = 2;

export async function handleFailedEmailTap(
  emailId: string,
  householdId: string,
): Promise<FailedEmailTapOutcome> {
  const state = await getEmailRetryState(emailId, householdId);
  if (!state) return 'open-review';
  if (
    state.status === 'RECEIVED' ||
    state.status === 'PROCESSING' ||
    state.status === 'HELD'
  ) {
    return 'reading';
  }
  if (
    state.status !== 'ERROR' ||
    state.failureCount >= FAILURES_BEFORE_REVIEW
  ) {
    return 'open-review';
  }
  const result = await retryEmail(emailId, householdId);
  if (result.retried) {
    // Same kick the Review card gives; the cron would pick it up within a
    // minute anyway.
    triggerProcessing().catch(() => {});
    return 'retried';
  }
  // Not re-read and still ERROR (the household's Gmail connection is
  // paused): nothing is reading it, so do not stay here as if something
  // were — Review shows it as Couldn't be read.
  if (result.status === 'ERROR') return 'open-review';
  return 'reading';
}

export async function getReviewItems(
  emailId: string,
  householdId: string,
): Promise<ReviewItem[]> {
  // Exclude dismissed items: emails-dismiss flips review_items.status to
  // 'dismissed' but leaves the rows in place for audit. Dismissed emails
  // (status PROCESSED) remain visible in the inbox list, so reopening one
  // would otherwise re-render the discarded items as if they were still
  // pending review. Committed items are intentionally still returned —
  // PROCESSED emails render in read-only mode so users can see what was
  // added to the schedule.
  const { data, error } = await supabase
    .from('review_items')
    .select('*')
    .eq('email_id', emailId)
    .eq('household_id', householdId)
    .neq('status', 'dismissed')
    .order('created_at', { ascending: true });

  if (error) throw error;
  const items = data ?? [];
  addBreadcrumb('review_items.fetched', {
    email_id: emailId,
    count: items.length,
  });
  return items;
}

// =============================================================================
// Proposed updates (context-aware extraction, Phase 1 — YAR-493)
// =============================================================================

/**
 * The schedule item a hinted review item proposes to update or cancel, in
 * the shape the Review card needs to render a before → after diff.
 */
export interface ProposalTarget {
  kind: 'event' | 'task';
  id: string;
  title: string;
  /** Event `start_date` or task `due_date`. */
  start: string | null;
  /** Event `end_date`; always null for a task. */
  end: string | null;
  location: string | null;
  childIds: string[];
  /**
   * Recovered from the review item the target was committed from — the flag
   * lives only on `review_items`, so a hand-made item reads as timed.
   */
  isTimeUnknown: boolean;
  /** Task status; null for an event. */
  taskStatus: TaskStatus | null;
  /**
   * The email the target was created from (events.email_id /
   * tasks.email_id); null for a hand-added item or when that email is gone.
   */
  emailId: string | null;
}

export interface ReviewItemsWithTargets {
  items: ReviewItem[];
  /** Keyed by the TARGET's id (event or task), not the review item's. */
  targets: Map<string, ProposalTarget>;
  /**
   * True when the targets could not be read. The card must not then pretend
   * a hinted item is new — Confirm would still update the existing one.
   */
  targetsFailed: boolean;
}

type ReviewItemProposalPatch = Pick<
  TablesUpdate<'review_items'>,
  | 'update_hint'
  | 'proposed_action'
  | 'supersedes_event_id'
  | 'supersedes_task_id'
>;

/** Every hint column back to "plain new item" (passes the hint CHECK). */
const CLEAR_PROPOSAL: ReviewItemProposalPatch = {
  update_hint: false,
  proposed_action: null,
  supersedes_event_id: null,
  supersedes_task_id: null,
};

/** A schedule row's embedded source review item (its time-unknown flag). */
export type EmbeddedSource = { is_time_unknown: boolean } | null;

function childIdsFrom(rows: Array<{ child_id: string }> | null): string[] {
  return (rows ?? []).map((r) => r.child_id);
}

/** What getProposalTargets reads from an item: its hint and target ids. */
export type ProposalTargetSource = Pick<
  ReviewItem,
  'update_hint' | 'supersedes_event_id' | 'supersedes_task_id'
>;

/**
 * Load the events and tasks that hinted review items point at: one batched
 * query per kind, each bound to the household explicitly (RLS alone would
 * admit every household the user belongs to). A target that is missing —
 * deleted since, or in another household — is simply absent from the map,
 * and the card falls back to a normal new item.
 */
export async function getProposalTargets(
  items: readonly ProposalTargetSource[],
  householdId: string,
): Promise<Map<string, ProposalTarget>> {
  const eventIds = new Set<string>();
  const taskIds = new Set<string>();
  for (const item of items) {
    if (!item.update_hint) continue;
    if (item.supersedes_event_id) eventIds.add(item.supersedes_event_id);
    if (item.supersedes_task_id) taskIds.add(item.supersedes_task_id);
  }

  const targets = new Map<string, ProposalTarget>();
  const [eventsRes, tasksRes] = await Promise.all([
    eventIds.size > 0
      ? supabase
          .from('events')
          .select(
            'id, title, start_date, end_date, location, email_id, event_children(child_id), source_review_item:review_items!source_review_item_id(is_time_unknown)',
          )
          .eq('household_id', householdId)
          .in('id', [...eventIds])
      : null,
    taskIds.size > 0
      ? supabase
          .from('tasks')
          .select(
            'id, title, due_date, location, status, email_id, task_children(child_id), source_review_item:review_items!source_review_item_id(is_time_unknown)',
          )
          .eq('household_id', householdId)
          .in('id', [...taskIds])
      : null,
  ]);

  if (eventsRes?.error) throw eventsRes.error;
  if (tasksRes?.error) throw tasksRes.error;

  for (const e of eventsRes?.data ?? []) {
    const source = e.source_review_item as EmbeddedSource;
    targets.set(e.id, {
      kind: 'event',
      id: e.id,
      title: e.title,
      start: e.start_date,
      end: e.end_date,
      location: e.location,
      childIds: childIdsFrom(e.event_children),
      isTimeUnknown: source?.is_time_unknown ?? false,
      taskStatus: null,
      emailId: e.email_id,
    });
  }
  for (const t of tasksRes?.data ?? []) {
    const source = t.source_review_item as EmbeddedSource;
    targets.set(t.id, {
      kind: 'task',
      id: t.id,
      title: t.title,
      start: t.due_date,
      end: null,
      location: t.location,
      childIds: childIdsFrom(t.task_children),
      isTimeUnknown: source?.is_time_unknown ?? false,
      taskStatus: t.status,
      emailId: t.email_id,
    });
  }

  addBreadcrumb('review_items.proposal_targets', {
    requested: eventIds.size + taskIds.size,
    found: targets.size,
  });
  return targets;
}

/**
 * Review items for one email, plus the schedule items any of them propose to
 * update or cancel. A failed target read does not lose the items: it is
 * reported through `targetsFailed` so the card can stay honest about what
 * Confirm will do.
 */
export async function getReviewItemsWithTargets(
  emailId: string,
  householdId: string,
): Promise<ReviewItemsWithTargets> {
  const items = await getReviewItems(emailId, householdId);
  try {
    const targets = await getProposalTargets(items, householdId);
    return { items, targets, targetsFailed: false };
  } catch {
    return { items, targets: new Map(), targetsFailed: true };
  }
}

/**
 * "Keep as new": the parent overrides a proposed update, so the item is
 * committed as a brand-new schedule item. The `keep_review_item_as_new` RPC
 * clears the same hint columns as before and stamps
 * `hint_cleared_by_parent_at`, so the override is recorded as the parent's
 * choice rather than looking like a match the safety net refused (Phase 3a).
 * Managers of `householdId` only (checked in the RPC); returns the row.
 */
export async function keepReviewItemAsNew(
  itemId: string,
  householdId: string,
): Promise<ReviewItem> {
  const { data: item, error } = await supabase
    .rpc('keep_review_item_as_new', {
      p_review_item_id: itemId,
      p_household_id: householdId,
    })
    .single();
  if (error) throw error;
  if (!item) throw new Error('keep_review_item_as_new returned no row');
  return item;
}

export async function getReviewCount(householdId: string): Promise<number> {
  const { data: inboxes, error: inboxError } = await supabase
    .from('inbox_addresses')
    .select('id')
    .eq('household_id', householdId);

  if (inboxError) throw inboxError;
  const inboxIds = (inboxes ?? []).map((i) => i.id);
  if (!inboxIds.length) return 0;

  const { count, error } = await supabase
    .from('emails')
    .select('id', { count: 'exact', head: true })
    .in('inbox_address_id', inboxIds)
    .eq('status', 'REVIEW_REQUIRED');

  if (error) throw error;
  return count ?? 0;
}

/**
 * How many items Yaruta found that still wait for review, across the
 * household's emails that are waiting for review (REVIEW_REQUIRED) — the
 * "[X] new items" on Today's card (YAR-478). Counts `pending` rows only:
 * items already confirmed or discarded out of a partly-reviewed email are
 * not "new" any more.
 */
export async function getPendingReviewItemCount(
  householdId: string,
): Promise<number> {
  const { count, error } = await supabase
    .from('review_items')
    .select('id, email:emails!email_id!inner(status)', {
      count: 'exact',
      head: true,
    })
    .eq('household_id', householdId)
    .eq('status', 'pending')
    .eq('email.status', 'REVIEW_REQUIRED');

  if (error) throw error;
  return count ?? 0;
}

/**
 * Resolve the SINGLE household the current user belongs to.
 *
 * YAR-269 (prospective): Yaruta enforces at most one household per account
 * going forward — `invitations-accept` rejects accepting a second
 * household, and the switcher UI that used to let a multi-household
 * account flip between them has been removed. This resolver reflects that
 * policy: it deterministically returns the account's OLDEST
 * `household_members` row (by `created_at`) — the same tie-break
 * `jobs-send-daily-digest` uses — so a legacy account that still carries
 * more than one row from before the guard shipped (no backfill runs
 * against those rows) sees ONE consistent household everywhere. New
 * accounts only ever have one row, so this is a no-op for them.
 *
 * The actual ordering lives in ONE place — household.service.ts's
 * `resolveActiveMembership` — shared with `user.service.ts`'s
 * `getUserRole` fallback so the household AppShell displays and the role
 * bootstrapped at auth time always agree, with no localStorage pin.
 *
 * Returns `null` if the user has no memberships at all.
 */
export async function getHouseholdIdForCurrentUser(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: dbUser, error: userError } = await supabase
    .from('users')
    .select('id')
    .eq('supabase_id', user.id)
    .maybeSingle();

  if (userError || !dbUser) return null;

  const membership = await resolveActiveMembership(dbUser.id);
  return membership?.householdId ?? null;
}

/** Fetch extraction summaries for a list of email IDs */
export async function getExtractionSummaries(
  emailIds: string[],
): Promise<Map<string, string>> {
  if (!emailIds.length) return new Map();

  const { data, error } = await supabase
    .from('email_extractions')
    .select('email_id, summary')
    .in('email_id', emailIds)
    .not('summary', 'is', null);

  if (error) throw error;

  const summaries = new Map<string, string>();
  for (const row of data ?? []) {
    if (row.email_id && row.summary) {
      summaries.set(row.email_id, row.summary);
    }
  }
  return summaries;
}

/**
 * Emails the current user has opened in Review, for one household (YAR-498).
 *
 * RLS already limits `email_reads` to the caller's own rows; the household
 * filter is what keeps another household's reads out of this feed (RLS
 * scope is every household you belong to, not the active one).
 *
 * Read state is a nicety, so a failure — including the deploy window before
 * the table exists — reads as "nothing opened yet" rather than breaking the
 * feed.
 */
export async function getReadEmailIds(
  householdId: string,
): Promise<Set<string>> {
  const { data, error } = await supabase
    .from('email_reads')
    .select('email_id')
    .eq('household_id', householdId);
  if (error) {
    console.warn('[email.service] email_reads read failed:', error.message);
    return new Set();
  }
  return new Set((data ?? []).map((row) => row.email_id));
}

/**
 * Marks an email read for the current user (YAR-498). Idempotent on the
 * server. Never throws: a failed write only means the dot comes back on the
 * next load, which must not get in the way of opening the item.
 */
export async function markEmailRead(emailId: string): Promise<void> {
  const { error } = await supabase.rpc('mark_email_read', {
    p_email_id: emailId,
  });
  if (error && !isMissingFunctionError(error)) {
    console.warn('[email.service] mark_email_read failed:', error.message);
  }
}

/** Fetch review item counts grouped by email_id for a list of email IDs */
export async function getReviewItemCounts(
  emailIds: string[],
): Promise<Map<string, number>> {
  if (!emailIds.length) return new Map();

  const { data, error } = await supabase
    .from('review_items')
    .select('email_id')
    .in('email_id', emailIds)
    .neq('status', 'dismissed');

  if (error) throw error;

  const counts = new Map<string, number>();
  for (const row of data ?? []) {
    counts.set(row.email_id, (counts.get(row.email_id) ?? 0) + 1);
  }
  return counts;
}

export async function createReviewItem(data: {
  email_id: string;
  household_id: string;
  type: 'task' | 'event';
  title: string;
  description?: string | null;
  /** Array of child names to assign. The first value is mirrored to
   *  review_items.child_name for legacy readers. */
  child_names?: string[];
  start_datetime?: string | null;
  end_datetime?: string | null;
  due_datetime?: string | null;
  location?: string | null;
  link?: string | null;
  links?: LinkEntry[];
  related_event_id?: string | null;
  is_time_unknown?: boolean;
  confidence?: 'low' | 'medium' | 'high';
  status?: 'pending' | 'committed' | 'dismissed';
}): Promise<ReviewItem> {
  const { child_names, links, link, ...rest } = data;
  const names = child_names ?? [];
  const { data: item, error } = await supabase
    .from('review_items')
    .insert({
      ...rest,
      links: links ?? [],
      link: scalarFromLinks(links ?? []) ?? link ?? null,
      child_name: names[0] ?? null,
      child_names: names,
      confidence: data.confidence ?? 'medium',
      status: data.status ?? 'pending',
    })
    .select()
    .single();
  if (error) throw error;
  return item;
}

export async function updateReviewItem(
  itemId: string,
  householdId: string,
  updates: {
    title?: string;
    description?: string | null;
    child_names?: string[];
    type?: 'task' | 'event';
    start_datetime?: string | null;
    end_datetime?: string | null;
    due_datetime?: string | null;
    location?: string | null;
    link?: string | null;
    links?: LinkEntry[];
    related_event_id?: string | null;
    is_time_unknown?: boolean;
    /**
     * Recurring events (YAR-443): the whole rule, written back with only
     * `until` changed. `null` would drop the rule entirely — the web never
     * sends it; clearing the end date sends the rule with `until: null`.
     */
    recurrence?: Recurrence;
  },
  /**
   * The item's type BEFORE this edit. When the edit changes it, any update /
   * cancel proposal is cleared in the same write: a proposal copies fields
   * onto a target of the OTHER kind only by accident (an event's start is
   * not a task's due date), so a re-typed item is committed as a new one.
   */
  currentType?: string,
): Promise<ReviewItem> {
  const { child_names, links, recurrence, ...rest } = updates;
  const typeChanged =
    updates.type !== undefined &&
    currentType !== undefined &&
    updates.type !== currentType;
  const dbUpdates = {
    ...rest,
    ...(child_names !== undefined
      ? { child_names, child_name: child_names[0] ?? null }
      : {}),
    ...(links !== undefined ? { links, link: scalarFromLinks(links) } : {}),
    ...(typeChanged ? CLEAR_PROPOSAL : {}),
  };
  const patch: ReviewItemPatch = {
    ...dbUpdates,
    ...(recurrence !== undefined
      ? { recurrence: recurrenceToJson(recurrence) }
      : {}),
  };
  const { data: item, error } = await supabase
    .from('review_items')
    .update(patch)
    .eq('id', itemId)
    .eq('household_id', householdId)
    .select()
    .single();
  if (error) throw error;
  return item;
}

/** What `dismiss_review_item` reports back (20261026164948). */
export interface ReviewItemDismissResult {
  /** False when the item was no longer pending — nothing was written. */
  dismissed: boolean;
  /** The `review_batches` row the discard was recorded under. */
  batchId: string | null;
  /**
   * Set when this discard took the email's LAST pending item: the email is
   * now PROCESSED with this disposition. Null while items remain.
   */
  emailDisposition: 'APPROVED' | 'DISMISSED' | null;
}

/**
 * Parse the `dismiss_review_item` RPC result. Shared with review.service's
 * set-aside, which goes through the same RPC.
 */
export function toDismissResult(data: unknown): ReviewItemDismissResult {
  if (typeof data !== 'object' || data === null) {
    return { dismissed: false, batchId: null, emailDisposition: null };
  }
  const dismissed = 'dismissed' in data && data.dismissed === true;
  const batchId =
    'batch_id' in data && typeof data.batch_id === 'string'
      ? data.batch_id
      : null;
  const disposition =
    'email_disposition' in data ? data.email_disposition : null;
  return {
    dismissed,
    batchId,
    emailDisposition:
      disposition === 'APPROVED' || disposition === 'DISMISSED'
        ? disposition
        : null,
  };
}

/**
 * Discard ONE review item. Goes through the `dismiss_review_item` RPC, which
 * soft-dismisses it (20261026164948): the row stays with status 'dismissed'
 * (hidden by getReviewItems), the discard is recorded as a review batch, and
 * when it was the email's last pending item the email is closed (PROCESSED,
 * reported as `emailDisposition`). It authorizes the caller itself (PRIMARY
 * or CO_ADMIN of the item's household).
 *
 * `.rpc()` resolves with `{ data: null, error }` on a database error rather
 * than throwing, so the error is inspected explicitly.
 */
export async function deleteReviewItem(
  itemId: string,
  householdId: string,
): Promise<ReviewItemDismissResult> {
  const { data, error } = await supabase.rpc('dismiss_review_item', {
    p_review_item_id: itemId,
  });
  if (error && isMissingFunctionError(error)) {
    // Deploy-window guard (lib/deployWindow.ts): the RPC is not on this
    // backend yet, so delete the way the previous release did.
    await legacyDeleteReviewItem(itemId, householdId);
    return { dismissed: true, batchId: null, emailDisposition: null };
  }
  if (error) throw error;
  return toDismissResult(data);
}

/**
 * The pre-RPC delete, verbatim from the previous release — ONLY for the
 * deploy window before `dismiss_review_item` exists. Delete once
 * 20260930002543 is everywhere.
 */
async function legacyDeleteReviewItem(
  itemId: string,
  householdId: string,
): Promise<void> {
  const { error } = await supabase
    .from('review_items')
    .delete()
    .eq('id', itemId)
    .eq('household_id', householdId);
  if (error) throw error;
}

export async function triggerProcessing(): Promise<void> {
  // Brief delay to let the webhook finish writing emails to the DB before the processing job reads them
  await new Promise((resolve) => setTimeout(resolve, 1500));
  const { error } = await invokeFunction('jobs-process-emails', {
    body: {},
  });
  if (error) {
    console.error('Failed to trigger processing:', error);
    throw error;
  }
}

/**
 * Drop the parts of `preprocessing_metadata` that are body content.
 *
 * `images` carries every `<img src>` in the email plus its alt text, copied
 * verbatim out of the body. The list endpoint deliberately withholds the body,
 * so shipping those is the same leak by another column — and on a flyer-heavy
 * inbox, whose bodies are almost entirely URL by character, it is a sizeable
 * payload for data no list surface renders.
 *
 * Everything else in the column is list-render metadata and stays.
 */
export function stripBodyDerived(
  metadata: Email['preprocessing_metadata'],
): Email['preprocessing_metadata'] {
  if (
    typeof metadata !== 'object' ||
    metadata === null ||
    Array.isArray(metadata)
  ) {
    return metadata;
  }
  const rest = { ...metadata };
  delete rest.images;
  return rest;
}
